/**
 * O que a árvore de comportamento enxerga e pode pedir (item 5.1): consultas ao mundo (quem está com a bola,
 * quem está livre, onde ficam os aros) e intenções (voar até um ponto, tocar um botão, carregar o arremesso).
 * O cérebro (`brain.ts`) transforma as intenções em comando a cada tick.
 */
import {
  BALL,
  PITCH,
  hoopCenters,
  type BotLevel,
  type BotLevelParams,
  type Position,
  type Side,
} from "@besenliga/shared";
import { DEFENDS, inKeeperArea, type AthleteState, type Vec3, type WorldState } from "@besenliga/sim";
import type { Traced } from "./bt.js";

/** O que o bot quer fazer até a próxima decisão. */
export interface Intent {
  /** Ponto para onde voar (null = ficar parado). */
  target: Vec3 | null;
  /** Diminuir a velocidade ao chegar (posicionamento) em vez de passar direto (perseguição). */
  arrive: boolean;
  turbo: boolean;
  /** Botões para tocar uma vez (passe, rebatida, soco, captura, habilidade). */
  tap: number;
  /** Começar a carregar o arremesso, por tantos ticks. */
  charge: number;
}

export interface Ctx extends Traced {
  w: Readonly<WorldState>;
  me: AthleteState;
  level: BotLevel;
  p: BotLevelParams;
  intent: Intent;
  /** Valor em [-1, 1) que só depende do tick, do bot e de `k`: o "acaso" do bot é determinístico. */
  noise(k: number): number;
}

export const v3 = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const len = (a: Vec3) => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
export const dist = (a: Vec3, b: Vec3) => len(sub(a, b));
export const flat = (a: Vec3, b: Vec3) => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));

/** Lado dos aros que o time ataca. */
export const attackSide = (a: AthleteState): Side => -DEFENDS[a.team] as Side;
export const goalOf = (side: Side): Vec3 => hoopCenters(side)[1] ?? v3(side * PITCH.hoopX, 18, 0);

export const holderOf = (c: Ctx): AthleteState | undefined => {
  const id = c.w.balls?.pelota.holder;
  return id ? c.w.athletes.find((a) => a.id === id) : undefined;
};
export const hasBall = (c: Ctx) => c.w.balls?.pelota.holder === c.me.id;
export const mates = (c: Ctx) => c.w.athletes.filter((a) => a.team === c.me.team && a.id !== c.me.id);
export const opponents = (c: Ctx) => c.w.athletes.filter((a) => a.team !== c.me.team);
export const byRole = (list: AthleteState[], role: Position) => list.filter((a) => a.role === role);

/** Posição da Pelota prevista `seconds` à frente (sem gravidade: basta para correr atrás dela). */
export function ballAhead(c: Ctx, seconds: number): Vec3 {
  const pel = c.w.balls?.pelota;
  if (!pel) return v3(0, 12, 0);
  if (pel.holder) return pel.position;
  return v3(
    pel.position.x + pel.velocity.x * seconds,
    Math.max(BALL.pelota.radius, pel.position.y + pel.velocity.y * seconds),
    pel.position.z + pel.velocity.z * seconds,
  );
}

/** Dos atletas de `list`, o mais perto de `p` (empate pelo id, para ser determinístico). */
export function closest(list: readonly AthleteState[], p: Vec3): AthleteState | undefined {
  let best: AthleteState | undefined;
  let bd = Infinity;
  for (const a of list) {
    const d = dist(a.position, p);
    if (d < bd || (d === bd && best && a.id < best.id)) {
      best = a;
      bd = d;
    }
  }
  return best;
}

/** Adversário mais perto de `a` (distância), para saber se está marcado. */
export function pressure(c: Ctx, a: AthleteState): number {
  let d = Infinity;
  for (const o of opponents(c)) if (o.stunTicks === 0) d = Math.min(d, dist(o.position, a.position));
  return d;
}

/** Quanto o vetor até `p` está alinhado com a vassoura (cosseno no plano do chão). */
export function facing(a: AthleteState, p: Vec3): number {
  const dx = p.x - a.position.x;
  const dz = p.z - a.position.z;
  const l = Math.sqrt(dx * dx + dz * dz);
  return l > 1e-6 ? (dx * a.heading.x + dz * a.heading.z) / l : 1;
}

/** Companheiro que pode receber e está livre e mais perto do aro atacado que eu. */
export function openMate(c: Ctx): AthleteState | undefined {
  const side = attackSide(c.me);
  const goal = goalOf(side);
  const myGoal = flat(c.me.position, goal);
  let best: AthleteState | undefined;
  let score = -Infinity;
  for (const m of mates(c)) {
    if (m.stunTicks > 0 || m.role !== "artilheiro") continue;
    const d = dist(m.position, c.me.position);
    if (d < 6 || d > BALL.pelota.passRange - 8) continue;
    const free = pressure(c, m);
    if (free < 6) continue;
    const s = free + (myGoal - flat(m.position, goal));
    if (s > score) {
      score = s;
      best = m;
    }
  }
  return best;
}

export const inOwnArea = (a: AthleteState) => inKeeperArea(a);
